import { useAppSelector } from 'app/hooks';
import { SelectedSystemType } from 'data/types';
import {
  useGetQuotableSystemsAccessoriesRecommendationsQuery,
  useGetSystemAccessoryTypesQuery
} from 'features/api/project-api';
import { useMemo } from 'react';
import { buildDuctedAccessoryPanels } from 'utils/ducted-accessories';
import { readDuctedAccessorySelections } from 'utils/ducted-accessory-selections';
import { computeDuctedLineSetRequiredLengths } from 'utils/ducted-line-set-length';
import { buildDuctedLineSetScopes } from 'utils/ducted-line-set-scopes';
import {
  attributePairRecommendations,
  toRequiredLineSetLengthRequest
} from 'utils/ducted-pair-recommendations';
import {
  ductedDuplicateNameIndexes,
  nameWithDuplicateIndex
} from 'utils/duplicate-system-names';

export function useDuctedAccessoryRecommendations(system: SelectedSystemType) {
  const selectedSystems = useAppSelector(
    (state) => state.systems.selectedSystems
  );
  const { existingSystems, structure } = useAppSelector(
    (state) => state.structure
  );
  const configurations = useAppSelector(
    (state) => state.configurations.configurations
  );

  const requiredLengths = useMemo(
    () =>
      computeDuctedLineSetRequiredLengths({
        system,
        existingSystems,
        structure,
        configurations
      }),
    [system, existingSystems, structure, configurations]
  );
  const requiredLineSetLengthFeet = useMemo(
    () => toRequiredLineSetLengthRequest(system, requiredLengths),
    [system, requiredLengths]
  );

  const { data: recommendationsData, isLoading: isLoadingRecommendations } =
    useGetQuotableSystemsAccessoriesRecommendationsQuery({
      uniqueIds: [system.uniqueId],
      ...(requiredLineSetLengthFeet ? { requiredLineSetLengthFeet } : {})
    });
  const { data: accessoryTypes, isLoading: isLoadingAccessoryTypes } =
    useGetSystemAccessoryTypesQuery();

  const recommendations = useMemo(
    () =>
      attributePairRecommendations(
        system,
        (recommendationsData ?? []).filter(
          (rec) => rec.systemUniqueId === system.uniqueId
        )
      ),
    [recommendationsData, system]
  );
  const lineSetScopes = useMemo(
    () =>
      buildDuctedLineSetScopes({ system, recommendations, requiredLengths }),
    [system, recommendations, requiredLengths]
  );
  const selections = useMemo(
    () => readDuctedAccessorySelections({ system, recommendations }),
    [system, recommendations]
  );
  const panels = useMemo(
    () =>
      buildDuctedAccessoryPanels({
        recommendations,
        accessoryTypes: accessoryTypes ?? [],
        selections,
        systemName: nameWithDuplicateIndex(
          system.name,
          ductedDuplicateNameIndexes(selectedSystems).get(system.id)
        ),
        units: system.configuration.condensers.map((condenser) => ({
          uniqueId: condenser.uniqueId,
          name: condenser.name
        }))
      }),
    [
      recommendations,
      accessoryTypes,
      selections,
      system.configuration.condensers,
      system.name,
      system.id,
      selectedSystems
    ]
  );

  return {
    recommendations,
    lineSetScopes,
    selections,
    panels,
    isLoading: isLoadingRecommendations || isLoadingAccessoryTypes
  };
}
